var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { Spin } from "../Spin/Spin";
import "./Player.scss";
import { EveVideoPlayer } from "@adm-media/evejs-react-player";
import { useSelector } from "react-redux";
import { selectDocumentVisibilityStatus } from "@adm-media/adam-store-plugin";
import { useMutationObservable } from "../../../hooks/useMutationObserver";
import { PLAYER_VALID_TEXT_TRACK_KINDS } from "../../../utils/constants";
// evejs-react-player >= 0.15 already drops a non-array `textTracks` value, entries
// with no usable `src`, and entries whose `kind` is not a real TextTrackKind
// (older builds instead iterated a bare VTT-url string character by character and
// let video.js coerce each to "subtitles", lighting up an empty CC button). We
// still pre-filter for the one thing the player deliberately does not police:
// chapters are a VOD-only feature, so when `allowChapters` is false (LIVE /
// pre-live) the kind:"chapters" track is dropped here and video.js hides the
// control-bar chapters button.
function normalizeTextTracks(textTracks, allowChapters) {
    if (!Array.isArray(textTracks))
        return [];
    var result = [];
    for (var _i = 0, textTracks_1 = textTracks; _i < textTracks_1.length; _i++) {
        var track = textTracks_1[_i];
        if (!track || typeof track !== "object" || typeof track.src !== "string" || track.src.trim() === "")
            continue;
        if (typeof track.kind !== "string" || !PLAYER_VALID_TEXT_TRACK_KINDS.includes(track.kind))
            continue;
        if (!allowChapters && track.kind === "chapters")
            continue;
        result.push({
            kind: track.kind,
            src: track.src,
            srclang: typeof track.srclang === "string" ? track.srclang : undefined,
            label: typeof track.label === "string" ? track.label : undefined,
            default: typeof track.default === "boolean" ? track.default : undefined
        });
    }
    return result;
}
function toPlayerSource(input, allowChapters) {
    var _a;
    var sources = ((_a = input.sources) !== null && _a !== void 0 ? _a : [])
        .filter(function (item) { return !!item.src; })
        .map(function (item) { return ({ src: item.src, type: item.type }); });
    return { sources: sources, poster: input.poster, textTracks: normalizeTextTracks(input.textTracks, allowChapters) };
}
/**
 * Where the fake-live timeline is right now, given the wall-clock instant it
 * started from. Fake live is a plain VOD file presented as a live stream, so the
 * position a viewer belongs at is always derived from the clock and never from
 * where the media element happens to be: that is what makes a pause behave the
 * way it does on a real live, where the stream keeps running without you.
 * Looping sources wrap around (the file is replayed as one continuous stream);
 * for a non-looping one there is no live left to join once the clock has run
 * past the end, so the caller is left to decide what to do.
 */
function fakeLivePosition(anchor, duration, loop) {
    if (anchor === undefined)
        return undefined;
    if (typeof duration !== "number" || Number.isNaN(duration) || duration <= 0)
        return undefined;
    var elapsed = (Date.now() - anchor) / 1000;
    if (elapsed < 0)
        return undefined;
    if (elapsed < duration)
        return elapsed;
    return loop ? elapsed % duration : undefined;
}
var Player = function (_a) {
    var currentTime = _a.currentTime, source = _a.source, onPlay = _a.onPlay, onPause = _a.onPause, onReadyStateChange = _a.onReadyStateChange, onPlayerReady = _a.onPlayerReady, onTimeUpdate = _a.onTimeUpdate, onAudioTrackChange = _a.onAudioTrackChange, onTextTrackChange = _a.onTextTrackChange, onVolumeChange = _a.onVolumeChange, isFakeLive = _a.isFakeLive, _b = _a.loop, loop = _b === void 0 ? false : _b, _c = _a.widescreen, widescreen = _c === void 0 ? false : _c, _d = _a.hideControls, hideControls = _d === void 0 ? false : _d, _e = _a.autoplay, autoplay = _e === void 0 ? false : _e, _f = _a.muted, muted = _f === void 0 ? true : _f, _g = _a.forceLiveUi, forceLiveUi = _g === void 0 ? false : _g, _h = _a.forcedLayout, forcedLayout = _h === void 0 ? false : _h, additionalUi = _a.additionalUi, hideSpeed = _a.hideSpeed, disableFullScreen = _a.disableFullScreen, poster = _a.poster, _j = _a.allowChapters, allowChapters = _j === void 0 ? true : _j;
    var playerRef = useRef();
    var wrapperRef = useRef();
    var documentVisibilityStatus = useSelector(selectDocumentVisibilityStatus);
    var _k = useState(undefined), playerReference = _k[0], setPlayerReference = _k[1];
    // Root DOM node video.js attaches state classes to (vjs-paused, vjs-user-active, ...).
    // evejs-react-player wraps its internal <video class="video-js"> as a child of the container we
    // hand it, so we look it up one level deeper than playerRef itself.
    var _l = useState(null), playerRootEl = _l[0], setPlayerRootEl = _l[1];
    // Wall-clock instant matching position 0 of the fake-live timeline, i.e. when
    // this "live" started. Captured on the first play of each source (now minus
    // the position already reached, so joining a file mid-way still anchors
    // correctly) and cleared whenever a new source is loaded.
    var fakeLiveAnchorRef = useRef(undefined);
    // Last source descriptor actually handed to the player, serialised. Call
    // sites rebuild the source prop on every render, and setSource() forwards
    // straight to video.js's .src(), which reloads the media from position 0 even
    // when the URL has not changed: without this comparison any re-render (a tab
    // switch is enough, the whole tree re-renders on visibilitychange) restarts
    // playback. Same guard the React wrapper of the player library uses.
    var lastAppliedSourceRef = useRef(undefined);
    // Previous value of documentVisibilityStatus, so the effect below can tell a
    // real hidden -> visible transition from its own first run.
    var previousVisibilityRef = useRef(undefined);
    useEffect(function () {
        var player = new EveVideoPlayer(playerRef.current, {
            autoplay: autoplay,
            muted: muted,
            loop: loop,
            widescreen: widescreen,
            hideControls: hideControls,
            hideSpeed: hideSpeed,
            // Picture-in-Picture stays disabled project-wide (native browser PiP is already
            // off in evejs-react-player; this hides the CSS PiP control-bar button too).
            pip: false
        });
        if (poster)
            player.setPoster(poster);
        if (currentTime !== undefined) {
            player.currentTime = currentTime;
        }
        setPlayerRootEl(playerRef.current.querySelector(".video-js"));
        if (onPlayerReady) {
            onPlayerReady(player);
        }
        player.on("error", function (err) {
            var _a, _b, _c, _d;
            try {
                var src = void 0;
                try {
                    src = (_c = (_b = (_a = player.getSource()) === null || _a === void 0 ? void 0 : _a.sources) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.src;
                }
                catch (_e) { }
                Sentry.captureException(new Error((_d = err === null || err === void 0 ? void 0 : err.message) !== null && _d !== void 0 ? _d : "EveVideoPlayer error"), {
                    extra: {
                        code: err === null || err === void 0 ? void 0 : err.code,
                        source: src
                    }
                });
            }
            catch (_f) { }
        });
        player.on("play", function () { return onPlay && onPlay(player); });
        player.on("readystatechange", function (_a) {
            var readyState = _a.readyState;
            return onReadyStateChange && onReadyStateChange(player, readyState);
        });
        player.on("volumechange", function () { return onVolumeChange && onVolumeChange(player); });
        player.on("timeupdate", function () { return onTimeUpdate && onTimeUpdate(player); });
        setPlayerReference(player);
        return function () {
            player.dispose();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var onFullScreenCallback = useCallback(function (_a) {
        var isFullscreen = _a.isFullscreen;
        if (!isFullscreen || !disableFullScreen)
            return;
        try {
            playerReference === null || playerReference === void 0 ? void 0 : playerReference.exitFullscreen();
        }
        catch (_b) {
            console.warn("Error while exiting fullscreen");
        }
    }, [disableFullScreen, playerReference]);
    useEffect(function () {
        playerReference === null || playerReference === void 0 ? void 0 : playerReference.on("fullscreenchange", onFullScreenCallback);
        return function () { return playerReference === null || playerReference === void 0 ? void 0 : playerReference.off("fullscreenchange", onFullScreenCallback); };
    }, [playerReference, onFullScreenCallback]);
    // Anchors the fake-live timeline the first time each source plays. Done on
    // "play" rather than at construction because the anchor has to line up with
    // the media clock, which only exists once playback has actually started.
    var onPlayAnchorCallback = useCallback(function () {
        if (!isFakeLive || !playerReference || fakeLiveAnchorRef.current !== undefined)
            return;
        var position = playerReference.currentTime;
        if (typeof position !== "number" || Number.isNaN(position))
            return;
        fakeLiveAnchorRef.current = Date.now() - position * 1000;
    }, [isFakeLive, playerReference]);
    useEffect(function () {
        playerReference === null || playerReference === void 0 ? void 0 : playerReference.on("play", onPlayAnchorCallback);
        return function () { return playerReference === null || playerReference === void 0 ? void 0 : playerReference.off("play", onPlayAnchorCallback); };
    }, [playerReference, onPlayAnchorCallback]);
    // Fake live follows the tab: leaving it pauses, coming back resumes at the
    // point the live has got to in the meantime. Both halves have to be explicit.
    // The pause used to be left to the browser's own background-tab policy and the
    // resume was an accident of the source being re-issued on every re-render
    // (which reloaded the media, hence the restart from the beginning).
    useEffect(function () {
        if (!isFakeLive || !playerReference)
            return;
        var previous = previousVisibilityRef.current;
        previousVisibilityRef.current = documentVisibilityStatus;
        if (documentVisibilityStatus === "hidden") {
            playerReference.pause();
            return;
        }
        // First run with the tab already visible: autoplay (or the user) owns
        // playback, there is no live to catch up with yet.
        if (documentVisibilityStatus !== "visible" || previous !== "hidden")
            return;
        var position = fakeLivePosition(fakeLiveAnchorRef.current, playerReference.duration, loop);
        if (position !== undefined)
            playerReference.currentTime = position;
        Promise.resolve(playerReference.play()).catch(function () { });
    }, [documentVisibilityStatus, isFakeLive, playerReference, loop]);
    var onPauseCallback = useCallback(function () {
        // document.visibilityState, not just the store mirror of it: this handler is
        // re-registered on every visibility change, so the one attached when the tab
        // is being left can still be the copy that captured "visible", and resuming
        // there would undo the pause the effect above has just asked for.
        var tabIsVisible = documentVisibilityStatus === "visible" && document.visibilityState === "visible";
        if (isFakeLive && tabIsVisible && playerReference) {
            if (typeof (playerReference === null || playerReference === void 0 ? void 0 : playerReference.duration) === "number" &&
                typeof (playerReference === null || playerReference === void 0 ? void 0 : playerReference.currentTime) === "number" &&
                !Number.isNaN(playerReference === null || playerReference === void 0 ? void 0 : playerReference.duration) &&
                !Number.isNaN(playerReference === null || playerReference === void 0 ? void 0 : playerReference.currentTime) &&
                (playerReference === null || playerReference === void 0 ? void 0 : playerReference.duration) > (playerReference === null || playerReference === void 0 ? void 0 : playerReference.currentTime)) {
                // Pausing a live stream does not hold the stream. Resume where the
                // live has got to in the meantime rather than where the media was
                // left, which is also what keeps a reloaded source (setSource puts
                // video.js back to position 0) from looking like a restart.
                var position = fakeLivePosition(fakeLiveAnchorRef.current, playerReference.duration, loop);
                if (position !== undefined)
                    playerReference.currentTime = position;
                playerReference === null || playerReference === void 0 ? void 0 : playerReference.play();
            }
        }
        else
            onPause && onPause(playerReference);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFakeLive, playerReference, documentVisibilityStatus, loop]);
    useEffect(function () {
        playerReference === null || playerReference === void 0 ? void 0 : playerReference.on("pause", onPauseCallback);
        return function () { return playerReference === null || playerReference === void 0 ? void 0 : playerReference.off("pause", onPauseCallback); };
    }, [playerReference, onPauseCallback]);
    var audioTrackChangeCallback = useCallback(function (_a) {
        var track = _a.track;
        onAudioTrackChange && playerReference && onAudioTrackChange(playerReference, track);
    }, [onAudioTrackChange, playerReference]);
    var ccTrackChangeCallback = useCallback(function () {
        onTextTrackChange && playerReference && onTextTrackChange(playerReference);
    }, [onTextTrackChange, playerReference]);
    useEffect(function () {
        playerReference === null || playerReference === void 0 ? void 0 : playerReference.on("texttrackchange", ccTrackChangeCallback);
        return function () { return playerReference === null || playerReference === void 0 ? void 0 : playerReference.off("texttrackchange", ccTrackChangeCallback); };
    }, [playerReference, ccTrackChangeCallback]);
    useEffect(function () {
        playerReference === null || playerReference === void 0 ? void 0 : playerReference.on("audiotrackchange", audioTrackChangeCallback);
        return function () { return playerReference === null || playerReference === void 0 ? void 0 : playerReference.off("audiotrackchange", audioTrackChangeCallback); };
    }, [playerReference, audioTrackChangeCallback]);
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f;
        if (!source || !playerReference)
            return;
        var nextSource = toPlayerSource(source, allowChapters);
        // evejs-react-player's setSource() forwards straight to video.js's `.src()`, and an empty
        // sources array there raises MEDIA_ERR_SRC_NOT_SUPPORTED ("No compatible source was
        // found for this media"). Card-preview call sites intentionally pass `source={{}}`
        // together with a poster to show a static image with no video — guard against that
        // instead of handing video.js an empty array.
        if (nextSource.sources.length === 0) {
            playerReference.pause();
            return;
        }
        // Nothing to do when the descriptor is the one already loaded: see
        // lastAppliedSourceRef above. Kept after the empty-sources guard so a
        // poster-only state still pauses, and before the swap handling so an
        // unchanged source never reaches video.js's .src() at all.
        var nextSourceKey = JSON.stringify(nextSource);
        if (nextSourceKey === lastAppliedSourceRef.current)
            return;
        lastAppliedSourceRef.current = nextSourceKey;
        var currentSrc = (_c = (_b = (_a = playerReference.getSource()) === null || _a === void 0 ? void 0 : _a.sources) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.src;
        var nextSrc = (_d = nextSource.sources[0]) === null || _d === void 0 ? void 0 : _d.src;
        var isSwap = !!currentSrc && currentSrc !== nextSrc;
        if (isSwap) {
            // In-place source swaps are the pre-live -> live transition (progressive MP4 -> HLS)
            // and the multiple-source / audio-language switch. On Safari video.js keeps a single
            // tech instance across that swap and its native -> VHS source-handler hand-off races
            // VHS's segment loaders: a live fMP4 media segment gets transmuxed before its
            // EXT-X-MAP init segment is ready, so `segment.map` is undefined and VHS throws
            // ("undefined is not an object (evaluating 'tracks.text')"), leaving the player
            // frozen on the old frame. The player's own reset() tears the tech/media
            // element back to a clean no-source state (without disposing the player, so no
            // teardown race) which turns the next setSource() into a fresh load, and carries
            // the viewer's volume and mute state across the rebuild (video.js on its own
            // re-applies the constructor-time muted option, i.e. muted for autoplay, and puts
            // the volume back to 100%).
            try {
                var resettable = playerReference;
                if (typeof resettable.reset === "function")
                    resettable.reset();
                else
                    (_f = (_e = resettable.vjs) === null || _e === void 0 ? void 0 : _e.reset) === null || _f === void 0 ? void 0 : _f.call(_e);
            }
            catch (_g) {
                /* older evejs build with neither handle: fall through to setSource */
            }
        }
        playerReference.setSource(nextSource);
        // A new source is a new timeline: the next play re-anchors it.
        fakeLiveAnchorRef.current = undefined;
        // Safari/iOS does not re-honour the autoplay attribute on a programmatic source swap
        // the way Chrome does, so without this the video would sit paused on the previous frame.
        if (autoplay && isSwap) {
            Promise.resolve(playerReference.play()).catch(function () { });
        }
    }, [source, playerReference, allowChapters]);
    useEffect(function () {
        if (poster && playerReference)
            playerReference.setPoster(poster);
    }, [poster, playerReference]);
    var updateTextTracksPositionCallback = useCallback(function () {
        var htmlCollection = document.getElementsByClassName("vjs-text-track-display");
        var areControlsVisible = ((playerRootEl === null || playerRootEl === void 0 ? void 0 : playerRootEl.classList.contains("vjs-user-active")) || (playerRootEl === null || playerRootEl === void 0 ? void 0 : playerRootEl.classList.contains("vjs-paused"))) &&
            (playerRootEl === null || playerRootEl === void 0 ? void 0 : playerRootEl.classList.contains("vjs-has-started"));
        if (areControlsVisible) {
            for (var _i = 0, htmlCollection_1 = htmlCollection; _i < htmlCollection_1.length; _i++) {
                var element = htmlCollection_1[_i];
                element === null || element === void 0 ? void 0 : element.classList.remove("bottom-0");
            }
        }
        else {
            for (var _a = 0, htmlCollection_2 = htmlCollection; _a < htmlCollection_2.length; _a++) {
                var element = htmlCollection_2[_a];
                element === null || element === void 0 ? void 0 : element.classList.add("bottom-0");
            }
        }
    }, [playerRootEl]);
    useMutationObservable(playerRootEl, updateTextTracksPositionCallback);
    // Handle loop
    useEffect(function () {
        if (playerReference) {
            playerReference.loop = loop;
        }
    }, [loop, playerReference]);
    // Handle currentTime
    useEffect(function () {
        if (playerReference && currentTime !== undefined) {
            playerReference.currentTime = currentTime;
        }
    }, [currentTime, playerReference]);
    useEffect(function () {
        // Disable context menu in player
        wrapperRef.current.addEventListener("contextmenu", function (e) {
            e.preventDefault();
            e.stopPropagation();
        }, true);
    }, [wrapperRef]);
    // Listen for programmatically unmute with button (within Wrapper)
    useEffect(function () {
        if (playerReference) {
            playerReference.muted = muted;
        }
    }, [muted, playerReference]);
    return (_jsxs("div", __assign({ ref: wrapperRef, className: classNames("player-wrapper", {
            "force-live": forceLiveUi,
            "player-wrapper--disable-fullscreen": disableFullScreen
        }) }, { children: [forcedLayout && (_jsx("div", __assign({ className: "player-cover-layout" }, { children: _jsx(Spin, __assign({ className: "player-spinner", size: "large", spinning: true }, { children: _jsx("div", {}) })) }))), _jsx("div", { className: "player-container ".concat(widescreen ? "vjsp-widescreen" : "", " ").concat(hideControls ? "hide-controls" : "", " ").concat(hideSpeed ? "hide-speed" : ""), ref: playerRef }), additionalUi] })));
};
export default Player;
